import { useCallback, useRef } from 'react';
import { Checkbox, CheckboxRef } from 'antd';
import {
  SystemClassifierOption,
  useSystemClassifierSelectOptions,
} from '@atlas-frontend/sdk';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './BusinessLogFilterPane.translations.json';

const { addTranslations, useTranslation } = i18nNsFactory(
  'business-log-filter-pane'
);
addTranslations(translations);

function allOptions(logTypeOptions: SystemClassifierOption[]) {
  return ['0'].concat(
    ...logTypeOptions.map((option) => option.value.toString())
  );
}

interface BusinessLogFilterInputProps {
  onChange?: (value: string[]) => void;
  value?: string[];
}

export const BusinessLogTypeFilterInput = (
  props: BusinessLogFilterInputProps
) => {
  const { t } = useTranslation();
  const { value, onChange } = props;
  const allRef = useRef<CheckboxRef>(null);
  const logTypeOptions = useSystemClassifierSelectOptions([
    'BUSINESS_LOG_TYPE',
  ])['BUSINESS_LOG_TYPE'];

  const onClickAll = useCallback(
    (e: CheckboxChangeEvent) => {
      let selected: string[] = [];
      if (e.target.checked) {
        selected = allOptions(logTypeOptions);
      }
      onChange?.(selected);
    },
    [logTypeOptions, onChange]
  );
  const onSelect = useCallback(
    (selected: string[]) => {
      let filter = selected;

      const selectedTypes = new Set(selected);
      const isAllOptionSelected = allRef.current?.input?.checked ?? false;
      const allSelected = logTypeOptions.every((option) =>
        selectedTypes.has(option.value.toString())
      );

      if (!isAllOptionSelected && allSelected) {
        // If all options selected, select also the 'All' option
        filter.splice(0, 0, '0');
      } else if (isAllOptionSelected && !allSelected) {
        // If all options are not selected, deselect the 'All' option
        filter = selected.filter((value) => value !== '0');
      }
      onChange?.(filter);
    },
    [logTypeOptions, onChange]
  );

  return (
    <div>
      <Checkbox
        id={'type-option-all'}
        checked={value?.includes('0')}
        onChange={onClickAll}
        ref={allRef}
      >
        {t('field.type.all')}
      </Checkbox>
      <Checkbox.Group
        onChange={onSelect}
        style={{ display: 'inline' }}
        value={value}
      >
        {logTypeOptions.map((option) => (
          <Checkbox
            id={option.value.toString()}
            key={option.value}
            value={option.value.toString()}
          >
            {option.label}
          </Checkbox>
        ))}
      </Checkbox.Group>
    </div>
  );
};
